import { inject, onBeforeUnmount, onMounted } from 'vue'
import { routerKey } from 'vue-router'

/** The four create forms a funnel event is split by (the API's `FUNNEL_FORM_FLOWS`). */
export type FormFunnelFlow = 'task' | 'teammate' | 'team' | 'tool'

/** What a create view reports about its form (#1774). Every call is fire-and-forget: it never throws or waits. */
export type FormFunnel = {
    described(): void
    advancedOpened(): void
    fieldError(field: string): void
    completed(): void
}

/** The one report route for the six form events (`FormFunnelReportController`). */
const FORM_EVENTS_ROUTE = '/v1/funnel/form-events'

type FormReport = {
    event: string
    flow: FormFunnelFlow
    sessionId: string
    field?: string
    attempt?: number
    elapsedMs?: number
}

/** A version 4 UUID for one opened form: the server refuses a form session of any other shape. */
function formSessionId(): string {
    const native = globalThis.crypto?.randomUUID?.()
    if (native) {
        return native
    }
    const bytes = new Uint8Array(16)
    if (globalThis.crypto?.getRandomValues) {
        globalThis.crypto.getRandomValues(bytes)
    } else {
        for (let i = 0; i < bytes.length; i++) {
            bytes[i] = Math.floor(Math.random() * 256)
        }
    }
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * Sends one report, and never lets it throw, wait or block the form: the POST is not awaited, a failure is swallowed,
 * and `keepalive` lets an abandon sent as the page goes still arrive. The session says whose form it is, so only the
 * event, the flow, the form session and closed facts are sent: never a field's value or a description.
 */
function send(report: FormReport): void {
    try {
        void fetch(FORM_EVENTS_ROUTE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(report),
            keepalive: true,
        }).catch((): undefined => undefined)
    } catch {
        // A report that cannot even be sent changes nothing for the person filling in the form.
    }
}

/**
 * Reports a create form's funnel events for `flow` (#1774), under one form session per opened form:
 * - `form.started` when the view mounts;
 * - `form.described`, `form.advanced_opened` and `form.field_error_shown` as the view calls them, a field error once
 *   per call, numbered per field so each save attempt that shows it is its own report;
 * - `form.completed`, with the time since it opened, when the view saves;
 * - `form.abandoned`, with the time since it opened, once, when the form is left unsaved: the route moves away from
 *   the form, the page is hidden, or the view goes.
 *
 * A form session ends once, as completed or as abandoned, whichever comes first, and is never reported as both: the
 * stored rows do not carry the session, so nothing downstream could pair the two. A save that resolves after the
 * person has already left reports nothing more. A page brought back from the back/forward cache after it was hidden
 * unsaved is a form opened again: it starts a fresh form session, so a save made there is that session's completion.
 */
export function useFormFunnel(flow: FormFunnelFlow): FormFunnel {
    let sessionId = formSessionId()
    const router = inject(routerKey, null)
    const openedPath = router?.currentRoute.value.path ?? null
    const fieldErrors = new Map<string, number>()
    let openedAt = Date.now()
    let saved = false
    let left = false
    let stopRouteWatch: (() => void) | null = null

    const elapsedMs = () => Math.max(0, Date.now() - openedAt)

    function started(): void {
        openedAt = Date.now()
        send({ event: 'form.started', flow, sessionId })
    }

    function abandoned(): void {
        if (saved || left) {
            return
        }
        left = true
        send({ event: 'form.abandoned', flow, sessionId, elapsedMs: elapsedMs() })
    }

    const onPageHide = () => abandoned()

    // Only a page restored from the back/forward cache, and only one whose hidden form was reported abandoned.
    const onPageShow = (event: PageTransitionEvent) => {
        if (!event.persisted || !left) {
            return
        }
        sessionId = formSessionId()
        fieldErrors.clear()
        saved = false
        left = false
        started()
    }

    onMounted(() => {
        started()
        window.addEventListener('pagehide', onPageHide)
        window.addEventListener('pageshow', onPageShow)
        // Leaving the form by a navigation that went through, whether or not the view is rendered by a router view.
        stopRouteWatch =
            router?.afterEach((to, _from, failure) => {
                if (!failure && to.path !== openedPath) {
                    abandoned()
                }
            }) ?? null
    })

    onBeforeUnmount(() => {
        window.removeEventListener('pagehide', onPageHide)
        window.removeEventListener('pageshow', onPageShow)
        stopRouteWatch?.()
        stopRouteWatch = null
        abandoned()
    })

    return {
        described(): void {
            send({ event: 'form.described', flow, sessionId })
        },
        advancedOpened(): void {
            send({ event: 'form.advanced_opened', flow, sessionId })
        },
        fieldError(field: string): void {
            const attempt = (fieldErrors.get(field) ?? 0) + 1
            fieldErrors.set(field, attempt)
            send({ event: 'form.field_error_shown', flow, sessionId, field, attempt })
        },
        completed(): void {
            // Already saved, or already reported abandoned: the session has ended, so it is never counted twice.
            if (saved || left) {
                return
            }
            saved = true
            send({ event: 'form.completed', flow, sessionId, elapsedMs: elapsedMs() })
        },
    }
}
